/**
 * WebSocket 消息类型定义
 * 
 * 本文件定义前端使用的 WebSocket 消息类型接口
 * 用于剧本生成实时通信
 */

import type { ScriptGenerationStatus, ScriptShot } from './episodeScript';

/**
 * 任务类型枚举
 * 系统支持的所有任务类型
 */
export enum TaskType {
  IMAGE_GENERATION = 'image_generation',
  VIDEO_GENERATION = 'video_generation',
  AUDIO_GENERATION = 'audio_generation',
  MUSIC_GENERATION = 'music_generation',
  IMAGE_EDIT = 'image_edit',
  VIDEO_LIPSYNC = 'video_lipsync',
  EPISODE_SCRIPT = 'episode_script',
  BATCH_RESOURCE = 'batch_resource',
  VIDEO_EXPORT = 'video_export',
  ASSET_GENERATION = 'asset_generation',  // 主体生成
  MV_AUDIO_ANALYSIS = 'mv_audio_analysis', // MV 音频分析
  MUSIC_EDITING = 'music_editing',  // 音乐编辑
  AUDIO_EDITING = 'audio_editing',  // 音频编辑
  AVATAR_TRAIN = 'avatar-train',    // 数字人训练
  AVATAR_VIDEO = 'avatar-video',    // 数字人视频生成
}

/**
 * 剧本相关的 WebSocket 消息类型枚举
 */
export enum ScriptMessageType {
  /** 剧本状态变化 */
  SCRIPT_STATUS_CHANGED = 'script:status_changed',
  /** 步骤进度更新 */
  SCRIPT_STEP_PROGRESS = 'script:step_progress',
  /** 分镜生成完成 */
  SCRIPT_SHOTS_GENERATED = 'script:shots_generated',
}

/**
 * 剧本状态变化消息
 * 当剧本生成状态发生变化时（idle → processing → completed/failed）广播
 * 
 * @example
 * {
 *   messageType: 'script:status_changed',
 *   scriptId: 'abc123',
 *   episodeId: 'episode456',
 *   oldStatus: 'idle',
 *   newStatus: 'processing',
 *   userId: 'user123',
 *   timestamp: '2024-01-01T00:00:00.000Z'
 * }
 */
export interface ScriptStatusChangedMessage {
  messageType: 'script:status_changed';
  scriptId: string;
  /** 所属剧集ID，用于episode级别的WebSocket订阅路由 */
  episodeId: string;
  oldStatus: ScriptGenerationStatus;
  newStatus: ScriptGenerationStatus;
  errorMessage?: string;
  userId: string;
  timestamp: string;
}

/**
 * 步骤进度消息
 * 当生成步骤开始、进度更新或完成时广播
 * 
 * @example
 * {
 *   messageType: 'script:step_progress',
 *   scriptId: 'abc123',
 *   episodeId: 'episode456',
 *   stepNumber: 1,
 *   stepName: '构思故事线',
 *   status: 'processing',
 *   progress: 50,
 *   userId: 'user123',
 *   timestamp: '2024-01-01T00:00:00.000Z'
 * }
 */
export interface ScriptStepProgressMessage {
  messageType: 'script:step_progress';
  scriptId: string;
  /** 所属剧集ID，用于episode级别的WebSocket订阅路由 */
  episodeId: string;
  /** 步骤编号 (1-6) */
  stepNumber: number;
  /** 步骤名称: '构思故事线' | '确定视觉风格' | '设计角色' | '设计道具' | '设计场景' | '生成分镜' */
  stepName: string;
  /** 步骤状态 */
  status: 'pending' | 'processing' | 'completed' | 'failed';
  /** 进度百分比 (0-100) */
  progress: number;
  userId: string;
  timestamp: string;
}

/**
 * 分镜生成消息
 * 当一批分镜生成完成时广播
 * 
 * @example
 * {
 *   messageType: 'script:shots_generated',
 *   scriptId: 'abc123',
 *   episodeId: 'episode456',
 *   newShots: [...],
 *   startIndex: 0,
 *   totalShotCount: 20,
 *   isComplete: false,
 *   userId: 'user123',
 *   timestamp: '2024-01-01T00:00:00.000Z'
 * }
 */
export interface ScriptShotsGeneratedMessage {
  messageType: 'script:shots_generated';
  scriptId: string;
  /** 所属剧集ID，用于episode级别的WebSocket订阅路由 */
  episodeId: string;
  /** 新生成的分镜数组 */
  newShots: ScriptShot[];
  /** 新分镜的起始索引（0-based） */
  startIndex: number;
  /** 预计总分镜数 */
  totalShotCount: number;
  /** 是否所有分镜都已生成 */
  isComplete: boolean;
  userId: string;
  timestamp: string;
}

/**
 * 所有剧本相关 WebSocket 消息的联合类型
 */
export type ScriptWebSocketMessage =
  | ScriptStatusChangedMessage
  | ScriptStepProgressMessage
  | ScriptShotsGeneratedMessage;

/**
 * 订阅层级枚举
 * 按生命周期划分的三个订阅层级
 */
export enum SubscriptionScope {
  USER = 'user',       // 用户级，永久
  PROJECT = 'project', // 项目级，进入项目时
  EPISODE = 'episode', // 剧集级，进入剧集时
}

/**
 * WebSocket 订阅类型
 */
export type SubscriptionType = 'task' | 'taskType' | 'batch' | 'episode' | 'project';

/**
 * WebSocket 订阅接口
 */
export interface Subscription {
  type: SubscriptionType;
  taskId?: string;
  taskType?: TaskType;
  batchId?: string;
  /** 剧集ID，用于episode级别的订阅 */
  episodeId?: string;
  /** 项目ID，用于project级别的订阅 */
  projectId?: string;
}
